import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Card, CardContent, Grid, Typography, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, TextField, InputAdornment, IconButton, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import InboxIcon from '@mui/icons-material/Inbox';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import http, { apiErrorMessage } from '../../Api/http';
import { useSelector } from 'react-redux';
import { GetMyRequests, CancelLeave } from '../../Api/Api';
import { SkeletonRows, ConfirmDialog, dangerBtn } from '../uiKit';
import SnackBar from './SnackBar';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_DARK = '#6246E0';
const PRIMARY_BORDER = '#C9BEFB';

const STATUS_META = {
    Requested: { label: 'Pending',  color: '#B45309', bg: '#E0F2FE', border: '#BAE6FD', icon: PendingIcon },
    Pending:   { label: 'Pending',  color: '#B45309', bg: '#E0F2FE', border: '#BAE6FD', icon: PendingIcon },
    Approved:  { label: 'Approved', color: '#6246E0', bg: '#F1EEFE', border: '#C9BEFB', icon: CheckCircleIcon },
    Declined:  { label: 'Rejected', color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', icon: CancelIcon },
    Rejected:  { label: 'Rejected', color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', icon: CancelIcon },
};

const LEAVE_TYPE_STYLE = {
    'Sick Leave':      { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
    'Casual Leave':    { color: '#6246E0', bg: '#F3F0FE', border: '#C9BEFB' },
    'Planned Leave':   { color: '#16A34A', bg: '#EFF5FF', border: '#C9BEFB' },
    'Emergency Leave': { color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
    'Medical Leave':   { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
};

const normaliseStatus = (s) => STATUS_META[s]?.label || s || 'Pending';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatApiDate = (raw) => {
    if (!raw) return '—';
    const ddmm = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw);
    if (ddmm) {
        const [, dd, mm, yyyy] = ddmm;
        const monthIdx = Number(mm) - 1;
        if (monthIdx >= 0 && monthIdx <= 11) return `${dd} ${MONTH_SHORT[monthIdx]} ${yyyy}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const sortKey = (raw) => {
    if (!raw) return '';
    const ddmm = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw);
    if (ddmm) return `${ddmm[3]}-${ddmm[2]}-${ddmm[1]}`;
    return String(raw);
};

// Parses dd-MM-yyyy (or any Date-parsable string) to a local midnight Date.
const parseLeaveDate = (raw) => {
    if (!raw) return null;
    const ddmm = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw);
    const d = ddmm ? new Date(Number(ddmm[3]), Number(ddmm[2]) - 1, Number(ddmm[1])) : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

// Pending leaves, or approved leaves that have not started yet, can be cancelled by the employee.
const canCancelLeave = (l) => {
    const status = normaliseStatus(l.status);
    if (status === 'Pending') return true;
    if (status !== 'Approved') return false;
    const start = parseLeaveDate(l.fromDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !!start && start >= today;
};

export default function MyLeaveStatusPage() {
    const user = useSelector((state) => state.auth);
    const userType = user?.userType;
    const isSuperAdmin = userType === 'superadmin';

    const [leaves, setLeaves] = useState([]);
    const [serverCounts, setServerCounts] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', status: 'success' });
    const notify = (message, status = 'success') => setSnack({ open: true, message, status });

    // Self-service and token-keyed — the server already scopes this to the
    // logged-in employee, so there's no employeeCode to filter by client-side
    // (that's what silently dropped the status on the employee's own screen).
    const fetchMyLeaves = async () => {
        setIsFetching(true);
        try {
            const res = await http.get(GetMyRequests);
            const body = res?.data || {};
            if (body.error) {
                setLeaves([]);
                setServerCounts(null);
                return;
            }
            const payload = body.data ?? body;
            const list = Array.isArray(payload.leaveRequests) ? payload.leaveRequests : [];
            const mapped = list.map((r) => ({
                leaveApplicationId: r.id ?? r.leaveApplicationId,
                leaveType: r.leaveTypeName ?? r.leaveType ?? '',
                fromDate: r.fromDate ?? '',
                toDate: r.toDate ?? '',
                workingDays: r.workingDays ?? r.days ?? r.duration ?? 0,
                reason: r.reason ?? '',
                appliedOn: r.requestedOn ?? r.appliedOn ?? r.createdOn ?? '',
                reviewedOn: r.reviewedOn ?? '',
                status: r.status ?? 'Pending',
                rejectReason: r.rejectReason ?? r.rejectionReason ?? '',
            }));
            setLeaves(mapped);
            const summary = payload.summary;
            setServerCounts(summary ? {
                Pending: Number(summary.pending) || 0,
                Approved: Number(summary.approved) || 0,
                Rejected: Number(summary.rejected) || 0,
            } : null);
        } catch (err) {
            console.error('Fetch my leave status failed:', err);
            setLeaves([]);
            setServerCounts(null);
        } finally {
            setIsFetching(false);
        }
    };

    const confirmCancel = async () => {
        if (!cancelTarget || cancelling) return;
        setCancelling(true);
        try {
            const res = await http.put(CancelLeave, null, { params: { leaveApplicationId: cancelTarget.leaveApplicationId } });
            if (res?.data?.error) {
                notify(res.data.message || 'Could not cancel this leave.', 'error');
            } else {
                notify(res?.data?.message || 'Leave cancelled.');
                setCancelTarget(null);
                await fetchMyLeaves();
            }
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not cancel this leave.'), 'error');
        } finally {
            setCancelling(false);
        }
    };

    useEffect(() => {
        if (isSuperAdmin) return;
        fetchMyLeaves();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin]);

    const sorted = useMemo(() => (
        [...leaves].sort((a, b) => {
            const ad = sortKey(a.appliedOn || a.createdOn || a.fromDate);
            const bd = sortKey(b.appliedOn || b.createdOn || b.fromDate);
            return bd.localeCompare(ad);
        })
    ), [leaves]);

    const counts = useMemo(() => {
        if (serverCounts) {
            return {
                total: serverCounts.Pending + serverCounts.Approved + serverCounts.Rejected,
                Pending:  serverCounts.Pending,
                Approved: serverCounts.Approved,
                Rejected: serverCounts.Rejected,
            };
        }
        const acc = { total: sorted.length, Pending: 0, Approved: 0, Rejected: 0 };
        sorted.forEach(l => { acc[normaliseStatus(l.status)] = (acc[normaliseStatus(l.status)] || 0) + 1; });
        return acc;
    }, [sorted, serverCounts]);

    if (isSuperAdmin) {
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '7px',
                        bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <HistoryToggleOffIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                            My Leave Requests
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                            Track the status of leave applications you have submitted.
                        </Typography>
                    </Box>
                </Box>
                <Card sx={{ border: '1px solid #E5E7EB', borderRadius: '7px', boxShadow: 'none', bgcolor: '#fff' }}>
                    <CardContent sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.2 }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: '50%',
                                bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <VerifiedUserIcon sx={{ fontSize: 32, color: PRIMARY }} />
                            </Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#111827', textAlign: 'center' }}>
                                Not applicable for Super Admin
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#6B7280', textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
                                Super Admin leaves are auto-approved and do not need an approval workflow. This screen is for staff who submit leave requests for approval.
                            </Typography>
                            <Chip
                                label="Auto-approved"
                                icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                                sx={{
                                    mt: 0.6, bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK,
                                    border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, fontSize: 11,
                                    '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const q = search.trim().toLowerCase();
    const filtered = sorted.filter(l => {
        const status = normaliseStatus(l.status);
        if (statusFilter !== 'all' && status !== statusFilter) return false;
        if (!q) return true;
        return (
            (l.leaveType || '').toLowerCase().includes(q)
            || (l.reason || '').toLowerCase().includes(q)
            || (l.fromDate || '').toLowerCase().includes(q)
            || (l.toDate || '').toLowerCase().includes(q)
            || formatApiDate(l.fromDate).toLowerCase().includes(q)
            || formatApiDate(l.toDate).toLowerCase().includes(q)
        );
    });

    const statCards = [
        { key: 'all',      label: 'Total',    value: counts.total,    color: '#374151', bg: '#fff',       border: '#E5E7EB' },
        { key: 'Pending',  label: 'Pending',  value: counts.Pending,  color: '#0EA5E9', bg: '#E0F2FE',    border: '#BAE6FD', icon: PendingIcon },
        { key: 'Approved', label: 'Approved', value: counts.Approved, color: PRIMARY,   bg: PRIMARY_LIGHT, border: PRIMARY_BORDER, icon: CheckCircleIcon },
        { key: 'Rejected', label: 'Rejected', value: counts.Rejected, color: '#DC2626', bg: '#FEF2F2',    border: '#FECACA', icon: CancelIcon },
    ];

    const renderStatusChip = (s) => {
        const cfg = STATUS_META[s] || STATUS_META.Pending;
        const Icon = cfg.icon;
        return (
            <Chip
                size="small" label={cfg.label}
                icon={<Icon sx={{ fontSize: '12px !important' }} />}
                sx={{
                    bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                    fontWeight: 700, fontSize: '10.5px', height: 22,
                    '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
                }}
            />
        );
    };

    const renderLeaveTypeChip = (lt) => {
        const cfg = LEAVE_TYPE_STYLE[lt] || { color: '#374151', bg: '#F3F4F6', border: '#E5E7EB' };
        return (
            <Chip
                size="small" label={lt || '—'}
                sx={{
                    bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                    fontWeight: 600, fontSize: '10.5px', height: 22,
                }}
            />
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '7px',
                    bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <HistoryToggleOffIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                        My Leave Requests
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                        Track the status of leave applications you have submitted.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {statCards.map(card => {
                    const Icon = card.icon;
                    const active = statusFilter === card.key;
                    return (
                        <Grid size={{ xs: 6, sm: 3 }} key={card.label}>
                            <Card
                                onClick={() => setStatusFilter(card.key)}
                                sx={{
                                    cursor: 'pointer', borderRadius: '7px', boxShadow: 'none',
                                    border: `1px solid ${active ? card.color : card.border}`,
                                    bgcolor: card.bg,
                                    transition: 'transform 0.15s, box-shadow 0.15s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 16px ${card.color}22` },
                                }}
                            >
                                <CardContent sx={{ py: 1.4, '&:last-child': { pb: 1.4 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography sx={{
                                                fontSize: 11, color: card.color, fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: 0.4,
                                            }}>
                                                {card.label}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: 24, fontWeight: 800, color: '#111827',
                                                lineHeight: 1.2, mt: 0.4,
                                            }}>
                                                {card.value}
                                            </Typography>
                                        </Box>
                                        {Icon && (
                                            <Box sx={{
                                                width: 32, height: 32, borderRadius: '7px',
                                                bgcolor: '#fff', border: `1px solid ${card.border}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Icon sx={{ color: card.color, fontSize: 16 }} />
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Card sx={{ border: '1px solid #E5E7EB', borderRadius: '7px', boxShadow: 'none', bgcolor: '#fff' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                    <Box sx={{
                        display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
                        p: 1.2, mb: 1.5, borderRadius: '7px',
                        border: '1px solid #E5E7EB', bgcolor: '#FAFAFA',
                    }}>
                        <TextField
                            size="small"
                            placeholder="Search by type, reason or date..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.3 }}>
                                                <CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                },
                            }}
                            sx={{
                                flex: 1, minWidth: 220, maxWidth: 360,
                                '& .MuiOutlinedInput-root': {
                                    height: 34, fontSize: 12.5, borderRadius: '7px', bgcolor: '#fff',
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                                    '&.Mui-focused fieldset': { borderColor: PRIMARY },
                                },
                            }}
                        />
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>Showing</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: PRIMARY_DARK }}>
                                {filtered.length}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>of {sorted.length}</Typography>
                        </Box>
                    </Box>

                    <TableContainer sx={{ borderRadius: '7px', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: PRIMARY_LIGHT, borderBottom: `1px solid ${PRIMARY_BORDER}` }}>
                                    {['Leave Type', 'From', 'To', 'Days', 'Reason', 'Applied On', 'Status', 'Action'].map(h => (
                                        <TableCell key={h} sx={{
                                            fontWeight: 700, fontSize: 10, color: PRIMARY_DARK,
                                            textTransform: 'uppercase', whiteSpace: 'nowrap',
                                            letterSpacing: 0.6, py: 1.3, borderBottom: 'none',
                                        }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isFetching ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, borderBottom: 'none' }}>
                                            <SkeletonRows count={5} p={0} />
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, borderBottom: 'none' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
                                                <Box sx={{
                                                    width: 48, height: 48, borderRadius: '50%',
                                                    bgcolor: PRIMARY_LIGHT,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <InboxIcon sx={{ fontSize: 24, color: PRIMARY }} />
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                                    {sorted.length === 0 ? 'No leave requests yet' : 'No requests match your filters'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
                                                    {sorted.length === 0
                                                        ? 'When you submit a leave application it will appear here with its current status.'
                                                        : 'Try clearing the filters above.'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.map((l, idx) => (
                                    <TableRow
                                        key={l.leaveApplicationId || idx}
                                        sx={{
                                            '&:hover': { bgcolor: PRIMARY_LIGHT },
                                            transition: 'background-color 0.15s',
                                        }}
                                    >
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            {renderLeaveTypeChip(l.leaveType)}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                                                {formatApiDate(l.fromDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                                                {formatApiDate(l.toDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            {(() => {
                                                const days = l.workingDays ?? l.days ?? l.duration ?? 0;
                                                return (
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                        px: 0.8, py: 0.2, borderRadius: '7px',
                                                        bgcolor: '#EEF2FF', border: '1px solid #C7D2FE',
                                                    }}>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#4338CA', fontFamily: 'monospace' }}>
                                                            {days || '—'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 10, color: '#4338CA', fontWeight: 600 }}>
                                                            {Number(days) > 1 ? 'days' : 'day'}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6', maxWidth: 220 }}>
                                            <Tooltip title={l.reason || ''} arrow placement="top">
                                                <Typography sx={{
                                                    fontSize: 12, color: '#4B5563',
                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap', maxWidth: 220,
                                                }}>
                                                    {l.reason || '—'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>
                                                {formatApiDate(l.appliedOn || l.createdOn)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            {renderStatusChip(l.status)}
                                            {l.status !== 'Pending' && l.reviewedOn && (
                                                <Typography sx={{ fontSize: 10, color: '#9CA3AF', mt: 0.4 }}>
                                                    {formatApiDate(l.reviewedOn)}
                                                </Typography>
                                            )}
                                            {(() => {
                                                const isRejected = l.status === 'Declined' || l.status === 'Rejected';
                                                const reason = (l.rejectReason || l.rejectionReason || '').trim();
                                                if (!isRejected || !reason) return null;
                                                return (
                                                    <Tooltip title={`Reason: ${reason}`} arrow placement="top">
                                                        <Box sx={{
                                                            mt: 0.5, px: 0.8, py: 0.3, borderRadius: '7px',
                                                            bgcolor: '#FEF2F2', border: '1px solid #FECACA',
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                            maxWidth: 180, cursor: 'help',
                                                        }}>
                                                            <CancelIcon sx={{ fontSize: 11, color: '#B91C1C', flexShrink: 0 }} />
                                                            <Typography sx={{
                                                                fontSize: 10, color: '#B91C1C', fontWeight: 600,
                                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                            }}>
                                                                {reason}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                            {canCancelLeave(l) && l.leaveApplicationId ? (
                                                <Button
                                                    size="small" disabled={cancelling}
                                                    onClick={() => setCancelTarget(l)}
                                                    sx={{ ...dangerBtn, height: 28, fontSize: 11.5, px: 1.4 }}
                                                >
                                                    Cancel
                                                </Button>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <ConfirmDialog
                open={!!cancelTarget}
                onClose={() => { if (!cancelling) setCancelTarget(null); }}
                onConfirm={confirmCancel}
                title="Cancel this leave?"
                body={cancelTarget
                    ? `${cancelTarget.leaveType || 'Leave'} from ${formatApiDate(cancelTarget.fromDate)} to ${formatApiDate(cancelTarget.toDate)} will be cancelled${normaliseStatus(cancelTarget.status) === 'Approved' ? ' and the days restored to your balance' : ''}.`
                    : ''}
                confirmLabel={cancelling ? 'Cancelling...' : 'Cancel leave'}
            />
            <SnackBar open={snack.open} setOpen={(v) => setSnack((s) => ({ ...s, open: v }))} message={snack.message} status={snack.status} />
        </Box>
    );
}
